import { tableConfig } from 'view/dataTables';
import { dataConfig } from 'data/dataConfig';

//props generated by the api that shouldn't be shown to the user
const hiddenProperties = {
	created_at: true,
	updated_at: true,
	isOpen: true,
};

// Address / geospatial columns suppressed for Housing Litigations, but ONLY when the
// record is shown in a property context (the property page already shows where the
// building is). The standalone /hpd-housing-litigations/:id page shows no address of
// its own, so these must stay visible there or the record can't be identified.
const housingLitigationsPropertyScopedHiddenProperties = {
	housenumber: true,
	streetname: true,
	zip: true,
	boroid: true,
	block: true,
	lot: true,
	bin: true,
	bbl: true,
	latitude: true,
	longitude: true,
	nta: true,
	community_district: true,
	council_district: true,
	census_tract: true,
	casestatus: true, // raw concatenated form — show casestatus_label instead
	status: true, // derived Open/Closed bucket — CaseStatus label is user-facing
	numFiles: true,
	property_id: true,
};

export function getDetailsApiPath(dataSet) {
	if (dataConfig[dataSet].detailsUrl) {
		return dataConfig[dataSet].detailsUrl;
	}
	//if there isnt a detailsUrl prop, then its the same api path as the list endpoint
	return dataConfig[dataSet].urlPath;
}

export function getRecordTitle(dataSet) {
	return tableConfig[dataSet]?.singularTitle || 'Record';
}

//isPropertyScoped: true when the record is rendered alongside a known property (the
//details modal on the property page), false for the standalone record detail route
export function isPropertyHidden(propName, dataSet, isPropertyScoped = false) {
	if (hiddenProperties[propName]) {
		return true;
	}
	if (
		isPropertyScoped &&
		dataSet === 'housingLitigations' &&
		housingLitigationsPropertyScopedHiddenProperties[propName]
	) {
		return true;
	}
	return false;
}

// Abbreviations shown in capitals when a field name is humanized
const UPPERCASE_WORDS = new Set([
	'id', 'bin', 'bbl', 'ecb', 'dob', 'hpd', 'fdny', 'nyc', 'zip', 'url', 'oath',
	'bis', 'dos', 'sla', 'acris', 'nta', 'dot', 'dep', 'lpc', 'bsa', 'dhcr', 'llc',
	'gis', 'pdf', 'sro', 'ein', 'usps', 'co',
]);

// Source prefixes on combined records (odViolationNumber, bis_job_number)
const PREFIX_WORDS = { od: 'Open Data', bis: 'BIS' };

// field key -> the column label the data table already shows for it, per data set
const columnLabelCache = {};

function getColumnLabels(dataSet) {
	if (!dataSet || !tableConfig[dataSet]) {
		return {};
	}
	if (!columnLabelCache[dataSet]) {
		const labels = {};
		Object.values(tableConfig[dataSet]).forEach((sourceConfig) => {
			(sourceConfig?.columns || []).forEach((col) => {
				const label = typeof col.label === 'string' ? col.label.trim() : '';
				if (!label) return;
				if (col.key && !labels[col.key]) labels[col.key] = label;
				if (col.altKey && !labels[col.altKey]) labels[col.altKey] = label;
			});
		});
		columnLabelCache[dataSet] = labels;
	}
	return columnLabelCache[dataSet];
}

function humanizeKey(propName) {
	const words = String(propName)
		// Socrata turns "#" into "__" (job__ = "job #")
		.replace(/__+$/, ' #')
		.replace(/__+/g, ' # ')
		// camelCase and letter/digit boundaries
		.replace(/([a-z])([A-Z])/g, '$1 $2')
		.replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
		.replace(/([a-zA-Z])(\d)/g, '$1 $2')
		.split(/[\s_-]+/)
		.filter(Boolean);

	return words
		.map((word, i) => {
			const lower = word.toLowerCase();
			if (i === 0 && PREFIX_WORDS[lower] && words.length > 1) {
				return PREFIX_WORDS[lower];
			}
			if (UPPERCASE_WORDS.has(lower)) {
				return lower.toUpperCase();
			}
			return lower.charAt(0).toUpperCase() + lower.slice(1);
		})
		.join(' ');
}

//readable label for an api field name: the data table's own column label when it has
//one (e.g. last_action_date -> "Last Action Date"), otherwise the name humanized
export function prettifyPropName(propName, dataSet) {
	if (!propName) {
		return '';
	}
	return getColumnLabels(dataSet)[propName] || humanizeKey(propName);
}
